/**
 * web/src/hooks/mutations/useInviteUser.ts
 *
 * POST /api/admin/users/invite — admin-only. Pre-creates a User row so the
 * invitee can sign in via Google SSO on first attempt (OAuth callback is
 * whitelist-only — no auto-create).
 *
 * On success we append the canonical row returned by the server to the
 * qk.adminAccess() cache so the new user shows up in the roster immediately
 * (no extra refetch needed), then invalidate to converge on whatever the
 * server returns next.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { inviteUser } from '../../api/access';
import { toastApiError } from '../../lib/api';
import type { AdminAccessMatrix, AdminUser, Role } from '../../api/types';

/** Body for the mutation. */
export interface InviteUserBody {
  email: string;
  name: string;
  role: Role;
}

/**
 * React Query mutation hook for inviting a user.
 *
 * @returns React Query mutation handle yielding the created AdminUser
 * @sideEffects HTTP POST; appends the new row to the cached matrix; toast on outcome
 * @idempotent no — second invite with the same email throws 409
 */
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserBody) => inviteUser(body),
    onSuccess: (created: AdminUser) => {
      const previous = qc.getQueryData<AdminAccessMatrix>(qk.adminAccess());
      if (previous) {
        qc.setQueryData<AdminAccessMatrix>(qk.adminAccess(), {
          ...previous,
          users: [...previous.users, created].sort((a, b) => a.name.localeCompare(b.name)),
        });
      }
      toast.success(`Invited ${created.name}`);
    },
    onError: (err) => {
      toastApiError(err, 'Failed to invite user');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.adminAccess() });
    },
  });
}
